import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './shared/Button';
import { DownloadSection } from './shared/DownloadSection';
import { FeedbackWidget, ExpiredLinkIcon } from './shared/FeedbackWidget';
import { GiftBoxIcon } from './icons';
import { LoadingWidget } from './shared/LoadingWidget';
import { detectPlatform } from '../utils/platform';
import {
    appLinkPathFrom,
    buildAndroidIntentUrlForPath,
    buildIosDeepLinkForPath,
    buildPlayStoreUrlForPath,
    waitForAppOpen,
    APP_STORE_URL,
} from '../utils/deepLink';

type ScreenState = 'loading' | 'invalid' | 'clipboard-fallback' | 'other';

const APP_OPEN_TIMEOUT_MS = 1500;

/**
 * Browser landing for in-app route links (`/go/…`, e.g. a loyalty card at
 * `/go/loyalty/{restaurantId}`). Shown when the OS didn't hand the link to
 * the app: opens the app if it's installed, otherwise sends the user to the
 * store with the route attached so the app opens it after install
 * (install referrer on Android, clipboard on iOS).
 */
export const AppLinkRedirect: React.FC = () => {
    const { pathname } = useLocation();
    const path = appLinkPathFrom(pathname);
    const isLoyalty = path?.startsWith('/go/loyalty') ?? false;

    const [screenState, setScreenState] = useState<ScreenState>('loading');
    const [deepLink, setDeepLink] = useState('');
    const hasRun = useRef(false);

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;

        if (!path) {
            setScreenState('invalid');
            return;
        }

        const platform = detectPlatform();

        if (platform === 'android') {
            // Chrome falls back to the Play Store itself if the app isn't
            // installed; the timer covers browsers without intent:// support.
            window.location.href = buildAndroidIntentUrlForPath(path);
            waitForAppOpen(APP_OPEN_TIMEOUT_MS).then((opened) => {
                if (!opened) {
                    window.location.href = buildPlayStoreUrlForPath(path);
                }
            });
            return;
        }

        if (platform === 'ios') {
            const link = buildIosDeepLinkForPath(path);
            setDeepLink(link);
            window.location.href = link;
            waitForAppOpen(APP_OPEN_TIMEOUT_MS).then(async (opened) => {
                if (opened) return;
                try {
                    await navigator.clipboard.writeText(link);
                    window.location.href = APP_STORE_URL;
                } catch {
                    setScreenState('clipboard-fallback');
                }
            });
            return;
        }

        if (platform === 'mac') {
            const link = buildIosDeepLinkForPath(path);
            setDeepLink(link);
            (async () => {
                try {
                    await navigator.clipboard.writeText(link);
                    window.location.href = APP_STORE_URL;
                } catch {
                    setScreenState('clipboard-fallback');
                }
            })();
            return;
        }

        setScreenState('other');
    }, [path]);

    const handleContinueToAppStore = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(deepLink);
        } catch {
            console.error('Failed to copy deep link to clipboard');
        }
        window.location.href = APP_STORE_URL;
    }, [deepLink]);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            padding: '20px 16px',
            maxWidth: '400px',
            margin: '0 auto',
            boxSizing: 'border-box',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <img
                    src="/images/icons/pe.svg"
                    alt="Logo"
                    style={{ width: '40px', height: '40px' }}
                />
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {screenState === 'loading' && (
                    <LoadingWidget
                        message={isLoyalty ? 'Opening your loyalty card in Pebla...' : 'Opening Pebla...'}
                    />
                )}
                {screenState === 'invalid' && (
                    <FeedbackWidget
                        icon={<ExpiredLinkIcon />}
                        title="Invalid link"
                        message="This link is missing required information."
                    />
                )}
                {screenState === 'clipboard-fallback' && (
                    <ClipboardFallbackWidget
                        isLoyalty={isLoyalty}
                        onContinue={handleContinueToAppStore}
                    />
                )}
                {screenState === 'other' && <DownloadSection />}
            </div>
        </div>
    );
};

const ClipboardFallbackWidget: React.FC<{
    isLoyalty: boolean;
    onContinue: () => void;
}> = ({ isLoyalty, onContinue }) => (
    <div style={{
        textAlign: 'center',
        backgroundColor: '#161616',
        borderRadius: '16px',
        padding: '32px 24px',
        color: '#ffffff',
        width: '100%',
        boxSizing: 'border-box',
    }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            <GiftBoxIcon />
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
            {isLoyalty ? 'Your loyalty card is waiting' : 'Get Pebla'}
        </h2>
        <p style={{ fontSize: '14px', color: '#9ca3af', margin: '0 0 20px 0' }}>
            {isLoyalty
                ? 'Install Pebla and sign in with the same phone number to see your card.'
                : 'Install Pebla from the App Store to continue.'}
        </p>
        <Button onClick={onContinue}>Continue to App Store</Button>
    </div>
);
